import React,{useState} from 'react';
import Field from './Field';
import {acceptPublicQuote} from '../services/publicQuoteService';

const emailPattern=/^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatAcceptedTotal(cents) {
  return new Intl.NumberFormat('en-AU',{
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2
  }).format(cents / 100);
}

function formatAcceptedDate(value) {
  if (!value) {
    return '';
  }

  return new Intl.DateTimeFormat('en-AU',{
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
}

function AcceptanceSection({
  clientCompany,
  shareToken,
  selectedExtraIds,
  accepted,
  acceptedAt,
  onAccepted
}) {
  const [fullName,setFullName]=useState('');
  const [company,setCompany]=useState(clientCompany || '');
  const [email,setEmail]=useState('');
  const [notes,setNotes]=useState('');
  const [approved,setApproved]=useState(false);
  const [errors,setErrors]=useState({});
  const [submitting,setSubmitting]=useState(false);
  const [submitError,setSubmitError]=useState('');
  const [result,setResult]=useState(null);

  const validate=()=> {
    const nextErrors={};

    if (!fullName.trim()) {
      nextErrors.fullName='Please enter your full name.';
    }

    if (!company.trim()) {
      nextErrors.company='Please enter your company or organisation.';
    }

    if (!email.trim()) {
      nextErrors.email='Please enter your email address.';
    } else if (!emailPattern.test(email.trim())) {
      nextErrors.email='Please enter a valid email address.';
    }

    if (!approved) {
      nextErrors.approved='Please confirm that you approve this quotation.';
    }

    return nextErrors;
  };

  const handleSubmit=async (event)=> {
    event.preventDefault();
    setSubmitError('');

    const nextErrors=validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length>0) {
      return;
    }

    setSubmitting(true);

    try {
      const response=await acceptPublicQuote({
        shareToken,
        fullName,
        company,
        email,
        notes,
        selectedExtraIds
      });

      if (response?.already_accepted || response?.success===true) {
        setResult(response);
        onAccepted?.(response);
        return;
      }

      throw new Error('The acceptance RPC returned an unsuccessful response.');
    } catch (error) {
      console.error('Public quote acceptance failed:',error);
      setSubmitError("We couldn't record your approval. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const errorStyle={
    color: 'var(--accent-dark)',
    fontSize: '11px',
    lineHeight: 1.4
  };

  if (accepted || result?.success===true || result?.already_accepted===true) {
    const acceptedTotal=result?.total_inc_gst_cents;

    return (
      <section className="proposal-section acceptance">
        <div className="proposal-heading">
          <h2>Quotation accepted</h2>
        </div>

        <div className="acceptance-confirmation" role="status">
          <p>Thank you. Your approval has been recorded.</p>

          {acceptedAt && (
            <span>
              Accepted on {formatAcceptedDate(acceptedAt)}
            </span>
          )}

          {acceptedTotal !==undefined && (
            <strong>
              Accepted total including GST: {formatAcceptedTotal(acceptedTotal)}
            </strong>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="proposal-section acceptance">
      <div className="proposal-heading">
        <h2>Approve quotation</h2>
        <p>Complete the details below to approve this production quotation.</p>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="acceptance-fields">
          <Field label="Full Name">
            <input
              aria-describedby={errors.fullName ? 'acceptance-full-name-error' : undefined}
              aria-invalid={Boolean(errors.fullName)}
              onChange={(event)=> {
                setFullName(event.target.value);
                setErrors((current)=> ({...current,fullName: undefined}));
              }}
              placeholder="Your full name"
              required
              value={fullName}
            />
            {errors.fullName && (
              <span id="acceptance-full-name-error" style={errorStyle}>
                {errors.fullName}
              </span>
            )}
          </Field>

          <Field label="Company / Organisation">
            <input
              aria-describedby={errors.company ? 'acceptance-company-error' : undefined}
              aria-invalid={Boolean(errors.company)}
              onChange={(event)=> {
                setCompany(event.target.value);
                setErrors((current)=> ({...current,company: undefined}));
              }}
              required
              value={company}
            />
            {errors.company && (
              <span id="acceptance-company-error" style={errorStyle}>
                {errors.company}
              </span>
            )}
          </Field>

          <Field label="Email">
            <input
              aria-describedby={errors.email ? 'acceptance-email-error' : undefined}
              aria-invalid={Boolean(errors.email)}
              onChange={(event)=> {
                setEmail(event.target.value);
                setErrors((current)=> ({...current,email: undefined}));
              }}
              placeholder="name@company.com"
              required
              type="email"
              value={email}
            />
            {errors.email && (
              <span id="acceptance-email-error" style={errorStyle}>
                {errors.email}
              </span>
            )}
          </Field>

          <Field label="Additional Notes" optional className="field--wide">
            <textarea
              onChange={(event)=> setNotes(event.target.value)}
              placeholder="Add notes"
              rows="3"
              value={notes}
            />
          </Field>
        </div>

        <label className="approval-check">
          <input
            aria-describedby={errors.approved ? 'acceptance-approval-error' : undefined}
            checked={approved}
            onChange={(event)=> {
              setApproved(event.target.checked);
              setErrors((current)=> ({...current,approved: undefined}));
            }}
            type="checkbox"
          />
          <span>
            I approve this quotation and authorise Angus Ashton Film to proceed.
          </span>
        </label>

        {errors.approved && (
          <p id="acceptance-approval-error" style={errorStyle}>
            {errors.approved}
          </p>
        )}

        {submitError && (
          <p className="acceptance-error" role="alert">
            {submitError}
          </p>
        )}

        <button
          className="button button--accent button--accept"
          disabled={submitting}
          type="submit"
        >
          {submitting ? 'Accepting…' : 'Accept Quote'}
        </button>

        <p className="acceptance-note">
          Your approval is securely recorded against this quotation.
        </p>
      </form>
    </section>
  );
}

export default AcceptanceSection;